import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { EPISODE, SUSPECTS } from "../data/mystery";

/* ── SLIDE COMPONENTS ── */

function SlideWrapper({
  children,
  slideRef,
}: {
  children: React.ReactNode;
  slideRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div ref={slideRef} style={slideStyle}>
      <div style={noiseOverlay} />
      <div style={accentLine} />
      {children}
    </div>
  );
}

function Brand() {
  return (
    <div style={brandStyle}>
      H<span style={{ color: "#c8973a" }}>.</span>unch
    </div>
  );
}

function SlideCounter({ current, total }: { current: number; total: number }) {
  return (
    <div style={counterStyle}>
      {current} / {total}
    </div>
  );
}

function Slide1({ slideRef }: { slideRef: React.RefObject<HTMLDivElement> }) {
  return (
    <SlideWrapper slideRef={slideRef}>
      <div style={contentStyle}>
        <Brand />
        <div>
          <div style={eyebrowStyle}>— {EPISODE.season} · Episode {String(EPISODE.number).padStart(2, "0")}</div>
          <div style={titleStyle}>
            The<br />
            <em style={{ color: "#c8973a" }}>Guest</em><br />
            Room
          </div>
          <div style={taglineStyle}>{EPISODE.tagline}</div>
          <div style={dividerStyle} />
          <div style={sceneStyle}>
            <span style={{ color: "#c8973a", fontWeight: 500 }}>{EPISODE.timestamp}.</span>{" "}
            {EPISODE.crimeDescription}
          </div>
        </div>
        <div style={swipeStyle}>Swipe to meet the suspects →</div>
      </div>
      <SlideCounter current={1} total={5} />
    </SlideWrapper>
  );
}

function SuspectSlide({
  suspect,
  index,
  slideRef,
}: {
  suspect: typeof SUSPECTS[0];
  index: number;
  slideRef: React.RefObject<HTMLDivElement>;
}) {
  const questions: Record<string, string> = {
    adrian: "Close friend of the victim. First to \"take control\" when police arrived. Is he telling the truth?",
    victor: "Made the emergency call. Directed paramedics straight to the body. How did he know where to go?",
    marcus: `Was in the house all night. His kitchen knife set is missing one blade. Coincidence?`,
  };

  return (
    <SlideWrapper slideRef={slideRef}>
      <div style={contentStyle}>
        <Brand />
        <div style={{ position: "absolute", top: "14%", right: "6%", ...ghostNumberStyle }}>
          {String(index).padStart(2, "0")}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={slideLabelStyle}>— Suspect {String(index).padStart(2, "0")} of 03</div>
          <div style={suspectNameStyle}>{suspect.name}</div>
          <div style={suspectRoleStyle}>{suspect.role}</div>
          <div style={statementWrapStyle}>
            <div style={statementStyle}>"{suspect.statement}"</div>
          </div>
        </div>
        <div style={questionStyle}>
          {questions[suspect.id]}
        </div>
      </div>
      <SlideCounter current={index + 1} total={5} />
    </SlideWrapper>
  );
}

function Slide5({ slideRef }: { slideRef: React.RefObject<HTMLDivElement> }) {
  return (
    <SlideWrapper slideRef={slideRef}>
      <div style={contentStyle}>
        <Brand />
        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <div style={slideLabelStyle}>— The Question</div>
          <div style={ctaQuestionStyle}>
            Who<br />killed<br />
            <em style={{ color: "#c8973a" }}>Ethan?</em>
          </div>
          <div style={chipsRowStyle}>
            {SUSPECTS.map((s) => (
              <div key={s.id} style={chipStyle}>{s.name}</div>
            ))}
          </div>
        </div>
        <div>
          <div style={ctaDividerStyle} />
          <div style={ctaBlockStyle}>
            <span style={ctaTextStyle}>Read the full case. Make your accusation.</span>
            <span style={{ color: "#0e0c0a", fontSize: "1rem" }}>→</span>
          </div>
          <div style={bioStyle}>
            Full episode at <strong style={{ color: "#c8973a" }}>hunch.com</strong> · Link in bio
          </div>
        </div>
      </div>
      <SlideCounter current={5} total={5} />
    </SlideWrapper>
  );
}

/* ── MAIN PAGE ── */

export default function CarouselPage() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const refs = [
    useRef<HTMLDivElement>(null!),
    useRef<HTMLDivElement>(null!),
    useRef<HTMLDivElement>(null!),
    useRef<HTMLDivElement>(null!),
    useRef<HTMLDivElement>(null!),
  ];

  const slideNames = [
    `hunch-ep${String(EPISODE.number).padStart(2, "0")}-slide1-crime`,
    `hunch-ep${String(EPISODE.number).padStart(2, "0")}-slide2-${SUSPECTS[0].id}`,
    `hunch-ep${String(EPISODE.number).padStart(2, "0")}-slide3-${SUSPECTS[1].id}`,
    `hunch-ep${String(EPISODE.number).padStart(2, "0")}-slide4-${SUSPECTS[2].id}`,
    `hunch-ep${String(EPISODE.number).padStart(2, "0")}-slide5-cta`,
  ];

  const captureSlide = async (index: number) => {
    const el = refs[index].current;
    if (!el) return null;

    // Temporarily make visible for capture
    const prev = el.style.display;
    el.style.display = "flex";
    await new Promise((r) => setTimeout(r, 150));

    const canvas = await html2canvas(el, {
      useCORS: true,
      backgroundColor: "#0e0c0a",
      logging: false,
      scale: 2,
    } as any);

    el.style.display = prev;
    return canvas;
  };

  const downloadCanvas = (canvas: HTMLCanvasElement, name: string) => {
    const link = document.createElement("a");
    link.download = `${name}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const downloadCurrent = async () => {
    setStatus("Generating...");
    const canvas = await captureSlide(current);
    if (canvas) {
      downloadCanvas(canvas, slideNames[current]);
      setStatus("Downloaded.");
      setTimeout(() => setStatus(""), 2000);
    }
  };

  const downloadAll = async () => {
    setIsExporting(true);
    setProgress(0);

    for (let i = 0; i < 5; i++) {
      setStatus(`Generating slide ${i + 1} of 5...`);
      setProgress(Math.round((i / 5) * 100));
      const canvas = await captureSlide(i);
      if (canvas) {
        downloadCanvas(canvas, slideNames[i]);
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    setProgress(100);
    setStatus("✓ All 5 slides downloaded");
    setTimeout(() => {
      setIsExporting(false);
      setStatus("");
      setProgress(0);
    }, 3000);
  };

  const slides = [
    <Slide1 key="s1" slideRef={refs[0]} />,
    <SuspectSlide key="s2" suspect={SUSPECTS[0]} index={1} slideRef={refs[1]} />,
    <SuspectSlide key="s3" suspect={SUSPECTS[1]} index={2} slideRef={refs[2]} />,
    <SuspectSlide key="s4" suspect={SUSPECTS[2]} index={3} slideRef={refs[3]} />,
    <Slide5 key="s5" slideRef={refs[4]} />,
  ];

  return (
    <div style={pageStyle}>

      {/* Page header */}
      <div style={pageHeaderStyle}>
        <div style={pageLogoStyle}>
          H<span style={{ color: "#c8973a" }}>.</span>unch
        </div>
        <div style={pageMetaStyle}>Carousel Generator · Episode {EPISODE.number}</div>
        <button style={backBtnStyle} onClick={() => navigate("/")}>
          ← Back to episode
        </button>
      </div>

      {/* Episode info */}
      <div style={episodeInfoStyle}>
        <span style={epLabelStyle}>{EPISODE.series}</span>
        <span style={epDivStyle} />
        <span style={epTitleStyle}>{EPISODE.title}</span>
      </div>

      {/* Slide preview */}
      <div style={previewWrapStyle}>
        {/* All slides rendered but only current visible */}
        {slides.map((slide, i) => (
          <div
            key={i}
            style={{ display: i === current ? "block" : "none", width: "100%" }}
          >
            {slide}
          </div>
        ))}

        {/* Hidden slides for capture — all mounted in DOM */}
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          {slides.map((slide, i) =>
            i !== current ? <div key={`hidden-${i}`}>{slide}</div> : null
          )}
        </div>
      </div>

      {/* Navigation dots */}
      <div style={dotsRowStyle}>
        <button style={navBtnStyle(current === 0)} disabled={current === 0} onClick={() => setCurrent(c => c - 1)}>
          ← Prev
        </button>
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              style={dotStyle(i === current)}
            />
          ))}
        </div>
        <button style={navBtnStyle(current === 4)} disabled={current === 4} onClick={() => setCurrent(c => c + 1)}>
          Next →
        </button>
      </div>

      {/* Download bar */}
      <div style={downloadBarStyle}>
        <div>
          <div style={downloadTitleStyle}>Export slides</div>
          <div style={downloadSubStyle}>1080 × 1080px · PNG · Instagram ready</div>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
          <button style={dlBtnSecondaryStyle} onClick={downloadCurrent} disabled={isExporting}>
            Download slide {current + 1}
          </button>
          <button style={dlBtnPrimaryStyle} onClick={downloadAll} disabled={isExporting}>
            {isExporting ? "Generating..." : "Download all 5"}
          </button>
        </div>
      </div>

      {/* Progress */}
      {(status || isExporting) && (
        <div style={progressWrapStyle}>
          <div style={progressBgStyle}>
            <div style={{ ...progressFillStyle, width: `${progress}%` }} />
          </div>
          <div style={progressLabelStyle}>{status}</div>
        </div>
      )}

      {/* Instructions */}
      <div style={instructionsStyle}>
        <p style={instructionsTextStyle}>
          <strong style={{ color: "#c8973a" }}>How to use:</strong> Click "Download all 5" to export all slides as 1080×1080px PNGs. Upload as an Instagram carousel in order.{" "}
          <strong style={{ color: "#c8973a" }}>Caption:</strong>{" "}
          <em>"Three suspects. One locked house. One killer. Can you solve it? Full episode at the link in bio."</em>
        </p>
      </div>
    </div>
  );
}

/* ── SLIDE STYLES (inline — required for html2canvas) ── */

const slideStyle: React.CSSProperties = {
  width: "540px",
  height: "540px",
  background: "#0e0c0a",
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Playfair Display', Georgia, serif",
};

const noiseOverlay: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  opacity: 0.03,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E")`,
  pointerEvents: "none",
  zIndex: 1,
};

const accentLine: React.CSSProperties = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "3px",
  background: "linear-gradient(90deg, #c8973a 0%, transparent 100%)",
  zIndex: 3,
};

const contentStyle: React.CSSProperties = {
  position: "relative",
  zIndex: 2,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "7%",
};

const brandStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "0.85rem",
  fontWeight: 900,
  letterSpacing: "-0.01em",
  color: "#c8973a",
  marginBottom: "auto",
};

const counterStyle: React.CSSProperties = {
  position: "absolute",
  top: "5%",
  right: "6%",
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.55rem",
  letterSpacing: "0.12em",
  color: "#7a7268",
  zIndex: 4,
};

const eyebrowStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.55rem",
  letterSpacing: "0.22em",
  textTransform: "uppercase" as const,
  color: "#7a7268",
  marginBottom: "0.5rem",
};

const titleStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "3.2rem",
  fontWeight: 900,
  lineHeight: 0.95,
  letterSpacing: "-0.03em",
  color: "#f4efe6",
  marginBottom: "0.75rem",
};

const taglineStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontStyle: "italic",
  fontSize: "0.75rem",
  color: "#7a7268",
  lineHeight: 1.5,
  marginBottom: "1rem",
  maxWidth: "80%",
};

const dividerStyle: React.CSSProperties = {
  width: "40px",
  height: "2px",
  background: "#c8973a",
  marginBottom: "1rem",
};

const sceneStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.58rem",
  lineHeight: 1.75,
  color: "#c8bfb0",
  letterSpacing: "0.02em",
};

const swipeStyle: React.CSSProperties = {
  marginTop: "auto",
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.5rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase" as const,
  color: "rgba(122,114,104,0.5)",
};

const slideLabelStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.52rem",
  letterSpacing: "0.22em",
  textTransform: "uppercase" as const,
  color: "#7a7268",
  marginBottom: "1.25rem",
};

const ghostNumberStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "5.5rem",
  fontWeight: 900,
  lineHeight: 0.85,
  color: "rgba(200,151,58,0.07)",
  letterSpacing: "-0.04em",
  pointerEvents: "none",
  userSelect: "none",
};

const suspectNameStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "2.2rem",
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: "-0.02em",
  color: "#f4efe6",
  marginBottom: "0.25rem",
};

const suspectRoleStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.55rem",
  letterSpacing: "0.15em",
  textTransform: "uppercase" as const,
  color: "#c8973a",
  marginBottom: "1.25rem",
};

const statementWrapStyle: React.CSSProperties = {
  borderLeft: "2px solid #c8973a",
  paddingLeft: "1rem",
  marginBottom: "1rem",
};

const statementStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontStyle: "italic",
  fontSize: "0.82rem",
  lineHeight: 1.65,
  color: "#c8bfb0",
};

const questionStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.52rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  color: "#7a7268",
  marginTop: "auto",
  paddingTop: "0.75rem",
  borderTop: "1px solid rgba(200,151,58,0.12)",
};

const ctaQuestionStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "2.8rem",
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: "-0.02em",
  color: "#f4efe6",
  marginBottom: "1rem",
};

const chipsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.4rem",
  flexWrap: "wrap" as const,
  marginBottom: "1rem",
};

const chipStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.5rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  padding: "3px 8px",
  border: "1px solid rgba(200,151,58,0.25)",
  color: "#7a7268",
};

const ctaDividerStyle: React.CSSProperties = {
  width: "100%",
  height: "1px",
  background: "rgba(200,151,58,0.2)",
  marginBottom: "1rem",
};

const ctaBlockStyle: React.CSSProperties = {
  background: "#c8973a",
  padding: "0.8rem 1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "0.6rem",
};

const ctaTextStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "0.8rem",
  fontWeight: 700,
  color: "#0e0c0a",
};

const bioStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.5rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "#7a7268",
};

/* ── PAGE STYLES ── */

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "#111",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "2rem 1rem 4rem",
  fontFamily: "'DM Sans', sans-serif",
  color: "#f4efe6",
};

const pageHeaderStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "560px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "1.25rem",
};

const pageLogoStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "1.1rem",
  fontWeight: 900,
  letterSpacing: "-0.02em",
  color: "#f4efe6",
};

const pageMetaStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.15em",
  textTransform: "uppercase" as const,
  color: "#7a7268",
};

const backBtnStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  background: "none",
  border: "1px solid rgba(200,151,58,0.25)",
  color: "#7a7268",
  padding: "5px 12px",
  cursor: "pointer",
  borderRadius: "1px",
};

const episodeInfoStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "560px",
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  marginBottom: "1rem",
};

const epLabelStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: "#c8973a",
};

const epDivStyle: React.CSSProperties = {
  width: "30px",
  height: "1px",
  background: "rgba(200,151,58,0.2)",
  display: "inline-block",
};

const epTitleStyle: React.CSSProperties = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#f4efe6",
};

const previewWrapStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "540px",
  position: "relative",
};

const dotsRowStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "540px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "1.25rem",
  gap: "1rem",
};

const navBtnStyle = (disabled: boolean): React.CSSProperties => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  background: "none",
  border: "1px solid rgba(200,151,58,0.25)",
  color: disabled ? "rgba(122,114,104,0.3)" : "#7a7268",
  padding: "0.55rem 1rem",
  cursor: disabled ? "default" : "pointer",
  borderRadius: "1px",
});

const dotStyle = (active: boolean): React.CSSProperties => ({
  width: active ? "18px" : "6px",
  height: "6px",
  borderRadius: active ? "3px" : "50%",
  background: active ? "#c8973a" : "rgba(200,151,58,0.2)",
  border: "none",
  cursor: "pointer",
  transition: "all 0.2s",
  padding: 0,
});

const downloadBarStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "540px",
  marginTop: "1rem",
  padding: "1rem 1.25rem",
  background: "rgba(200,151,58,0.05)",
  border: "1px solid rgba(200,151,58,0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "1rem",
  flexWrap: "wrap" as const,
};

const downloadTitleStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.68rem",
  color: "#f4efe6",
  letterSpacing: "0.06em",
  marginBottom: "2px",
};

const downloadSubStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.55rem",
  color: "#7a7268",
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
};

const dlBtnSecondaryStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  background: "none",
  border: "1px solid rgba(200,151,58,0.25)",
  color: "#7a7268",
  padding: "0.55rem 0.9rem",
  cursor: "pointer",
  borderRadius: "1px",
  whiteSpace: "nowrap" as const,
};

const dlBtnPrimaryStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  background: "#c8973a",
  border: "none",
  color: "#0e0c0a",
  padding: "0.55rem 1rem",
  cursor: "pointer",
  borderRadius: "1px",
  fontWeight: 500,
  whiteSpace: "nowrap" as const,
};

const progressWrapStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "540px",
  marginTop: "0.5rem",
};

const progressBgStyle: React.CSSProperties = {
  height: "3px",
  background: "rgba(200,151,58,0.15)",
  borderRadius: "2px",
  overflow: "hidden",
};

const progressFillStyle: React.CSSProperties = {
  height: "100%",
  background: "#c8973a",
  borderRadius: "2px",
  transition: "width 0.3s ease",
};

const progressLabelStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.55rem",
  color: "#7a7268",
  letterSpacing: "0.1em",
  marginTop: "0.4rem",
  textTransform: "uppercase" as const,
};

const instructionsStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "540px",
  marginTop: "1.5rem",
  padding: "1rem 1.25rem",
  border: "1px solid rgba(200,151,58,0.12)",
  background: "rgba(200,151,58,0.03)",
};

const instructionsTextStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
  fontSize: "0.6rem",
  letterSpacing: "0.06em",
  color: "#7a7268",
  lineHeight: 1.7,
};
